import PageLoader from "@/components/loader/PageLoader";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faCalendar,
  faImage,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store";
import { setAvatarUrl } from "@/store/modules/avatar";
import ContextualSaveBar from "../bars/ContextualSaveBar";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface OperatingHour {
  id: number;
  day: string;
  open_time: string;
  close_time: string;
  branch: number;
}

interface Branch {
  id: number;
  name: string;
  phone: string;
  email: string;
  delivery_cost?: number;
  base_distance?: number;
  delivery_cost_per_additional_mile?: number;
  max_distance?: number;
  delivery_feature: boolean;
  operating_hours: OperatingHour[];
}

const Settings = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch>({} as Branch);
  const [operatingHours, setOperatingHours] = useState<OperatingHour[]>([]);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [isAvatarUploaded, setIsAvatarUploaded] = useState(false);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState("");
  const dispatch = useDispatch();
  const ownerAvatar = useSelector((state: RootState) => state.avatar.url);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imgExtension, setImgExtension] = useState("");

  // Fetch branches by business name
  const fetchBranchesByBusinessName = useCallback(
    async (businessName: string) => {
      if (loading || !axiosInstance) return;

      try {
        const apiUrl = `${
          import.meta.env.VITE_APP_API_BASE_URL
        }api/v1/branch/?business_name=${businessName}`;
        const response = await axiosInstance.get(apiUrl);
        setBranches(response.data.results);
        const curBranch = response.data.results.find(
          (branch: Branch) => branch.id === user?.selectedBranch?.id,
        );
        setSelectedBranch(curBranch || ({} as Branch));
        if (curBranch) {
          setOperatingHours(curBranch.operating_hours);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    },
    [loading, axiosInstance, user],
  );

  // Trigger fetch branches on component mount or when axiosInstance changes
  useEffect(() => {
    const storedUserData = localStorage.getItem("user_data");
    const userData = storedUserData ? JSON.parse(storedUserData) : null;
    const businessName = userData?.owner?.business_name;

    if (businessName) {
      fetchBranchesByBusinessName(businessName);
    }
  }, [fetchBranchesByBusinessName]);

  const handleSendPasswordResetEmail = async () => {
    const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
      import.meta.env.VITE_APP_REDBIRDPOSBE_CHANGEPASSWORD
    }`;
    if (!apiUrl) {
      console.error("API URL is not defined.");
      toast.error(t("back.management.settings.configurationError"));
      return;
    }
    try {
      if (!loading && axiosInstance) {
        await axiosInstance.post(apiUrl, { email: user?.email });
        toast.success(t("back.management.settings.passwordResetEmailSent"));
      }
    } catch (error) {
      console.error("Error sending password reset email:", error);
      toast.error(t("back.management.settings.errorSendingPasswordResetEmail"));
    }
  };

  const fileToBase64 = (
    file: File,
    callback: (result: string | ArrayBuffer | null) => void,
  ): void => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => callback(reader.result);
    reader.onerror = (error) =>
      console.log("Error converting file to Base64:", error);
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.size > 20000000) {
        toast.error(t("back.management.settings.fileSizeTooLarge"));
        return;
      }
      setImgExtension(file.name.split(".")[1]);
      setAvatarPreviewUrl(URL.createObjectURL(file));
      setIsAvatarUploaded(true);

      fileToBase64(file, (base64Result: string | ArrayBuffer | null) => {
        if (typeof base64Result === "string") {
          const base64Data = base64Result.split(",")[1];
          setAvatar(base64Data);
        }
      });
      setIsDirty(true);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarPreviewUrl("");
    setIsAvatarUploaded(false);
    setAvatar(null);
    setIsDirty(false);
  };

  const handleUpdateAvatar = async () => {
    if (!isAvatarUploaded) {
      toast.error(t("back.management.settings.pleaseUploadImageFirst"));
      return;
    }
    const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
      import.meta.env.VITE_APP_REDBIRDPOSBE_OWNER
    }`;
    if (!apiUrl) {
      console.error("API URL is not defined.");
      toast.error(t("back.management.settings.configurationError"));
      return;
    }
    try {
      if (!loading && axiosInstance && avatar && user?.owner) {
        const ownerUrl = `${apiUrl}${user.owner.id}/`;
        setIsSaving(true);
        const response = await axiosInstance.patch(ownerUrl, {
          avatar: avatar,
          imgExtension: imgExtension,
        });
        dispatch(setAvatarUrl(response.data.avatar));
        toast.success(t("back.management.settings.avatarUpdatedSuccessfully"));
        setIsSaving(false);
        setIsDirty(false);
      }
    } catch (error) {
      console.error("Error updating avatar:", error);
      toast.error(t("back.management.settings.errorUpdatingAvatar"));
      setIsSaving(false);
    }
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h2 className="mb-4 text-2xl font-semibold">{t("back.management.settings.title")}</h2>

        {/* Owner's Information Section */}
        <div className="my-4 rounded-lg bg-white p-4 shadow">
          <h3 className="mb-2 text-lg font-semibold">{t("back.management.settings.ownersInformation")}</h3>
          <div className="grid sm:grid-cols-1 md:grid-cols-2">
            <p>
              <strong>{t("back.management.settings.username")}:</strong> {user?.username}
            </p>
            <p>
              <strong>{t("back.management.settings.email")}:</strong> {user?.email}
            </p>
            <p>
              <strong>{t("back.management.settings.branchesOwned")}:</strong> {user?.branches.length}
            </p>
          </div>
        </div>

        {/* Update Avatar Section */}
        <div className="my-4 rounded-lg bg-white p-4 shadow">
          <h3 className="mb-2 text-lg font-semibold">{t("back.management.settings.updateAvatar")}</h3>
          <div
            className={`block w-full border-2 ${
              isAvatarUploaded ? "border-solid" : "border-dotted"
            } relative flex h-48 cursor-pointer items-center justify-center rounded-md border-gray-300 shadow-sm hover:border-gray-500`}
          >
            <input
              id="avatarUpload"
              type="file"
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              onChange={handleAvatarUpload}
              accept="image/*"
            />
            {avatarPreviewUrl ? (
              <>
                <img
                  src={avatarPreviewUrl}
                  alt={t("back.management.settings.avatarPreview")}
                  className="max-h-full max-w-full p-2"
                />
                <button
                  onClick={handleRemoveAvatar}
                  className="absolute right-0 top-0 m-2 rounded-full bg-red-500 p-1 text-white"
                  style={{ width: "30px", height: "30px" }}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </>
            ) : (
              <div className="text-center">
                <FontAwesomeIcon
                  icon={faImage}
                  size="2x"
                  className="mb-2 text-gray-400"
                />
                <p className="mb-2 text-gray-600">
                  {t("back.management.settings.clickOrDragImage")}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Integrations Section */}
        <div className="my-4 rounded-lg bg-white p-4 shadow">
          <h3 className="mb-4 text-xl font-semibold">{t("back.management.settings.integrations")}</h3>
          <div
            onClick={() =>
              (window.location.href = "/integration/google-calendar")
            }
            className="flex w-fit cursor-pointer items-center gap-4 rounded-lg border border-gray-200 p-4 shadow-sm transition-all hover:shadow-md"
          >
            <div className="flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCalendar}
                className="text-2xl text-blue-500"
              />
            </div>
            <div>
              <h4 className="font-medium">{t("back.management.settings.googleCalendar")}</h4>
            </div>
          </div>
        </div>

        {/* Password Reset Email Section */}
        <div className="my-4 rounded-lg bg-white p-4 shadow">
          <h3 className="mb-2 text-lg font-semibold">{t("back.management.settings.passwordReset")}</h3>
          <p>
            {t("back.management.settings.passwordResetDescription")}
          </p>
          <button
            onClick={handleSendPasswordResetEmail}
            className="mt-4 inline-flex items-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {t("back.management.settings.sendPasswordResetEmail")}
          </button>
        </div>
      </div>
      <ContextualSaveBar
        isDirty={isDirty}
        onSave={handleUpdateAvatar}
        onDiscard={handleRemoveAvatar}
        isSaving={isSaving}
      />
    </PageLoader>
  );
};

export default Settings;
